import React, { Fragment } from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import { defineMessages, FormattedMessage } from 'react-intl'
import FormField from 'src/Components/FormField'
import Input from 'src/Components/Input'
import Text from 'src/Components/Text'
import Select from 'src/Components/Select'
import { FORM_FIELD_NAMES } from 'src/constants/affordability'
import { LOAN_TERMS } from 'src/constants/loan'
import TYPES from 'src/Components/FormField/types'
import PersistAffordabilityButton from 'src/features/buyers/Components/Global/AffordabilityModal/PersistAffordabilityButton'
import PriceCounter from './PriceCounter'

import styles from './CalculatePricePointTab.module.scss'

const propTypes = {
  className: PropTypes.string
}

const StyledText = ({ className, ...restProps }) => (
  <Text
    element='span'
    className={classnames(styles.text, className)}
    {...restProps}
  />
)

const Caption = props => <Text size='xs' color='metal' {...props} />

const StyledInput = props => <Input className={styles.input} {...props} />

const StyledSelect = props => <Select className={styles.select} {...props} />

const loanTermMessages = defineMessages({
  value: {
    id: 'Affordability.form.loanTermDropdownValue',
    defaultMessage: '{loanTermYears, number}-year'
  }
})

const termOptions = intl =>
  LOAN_TERMS.map(term => ({
    key: term,
    value: intl.formatMessage(loanTermMessages.value, {
      loanTermYears: term
    })
  }))

const CalculatePricePointTab = ({
  className,
  valid,
  onChange,
  income,
  debt,
  downPayment,
  loanTerm,
  intl
}) => (
  <Fragment>
    <FormField
      data-qa='inputIncome'
      input={StyledInput}
      labelText={StyledText}
      className={styles.field}
      name={FORM_FIELD_NAMES.INCOME}
      type={TYPES.NUMBER}
      onChange={onChange}
      value={income}
      placeholder='$0'
      min='0'
      step='1'
      required
    >
      <FormattedMessage
        id='Affordability.form.incomeLabel'
        defaultMessage='Annual household income'
      />
      <Caption>
        <FormattedMessage
          id='Affordability.form.incomeSublabel'
          defaultMessage='The total combined income of every adult in the household'
        />
      </Caption>
    </FormField>

    <FormField
      data-qa='inputDebt'
      input={StyledInput}
      labelText={StyledText}
      className={styles.field}
      name={FORM_FIELD_NAMES.DEBT}
      type={TYPES.NUMBER}
      onChange={onChange}
      value={debt}
      placeholder='$0'
      min='0'
      step='1'
      required
    >
      <FormattedMessage
        id='Affordability.form.debtLabel'
        defaultMessage='Monthly debt you pay'
      />
      <Caption>
        <FormattedMessage
          id='Affordability.form.debtSublabel'
          defaultMessage='Car payments, credit card payments, other non-rent or household debt'
        />
      </Caption>
    </FormField>

    <FormField
      data-qa='inputDownPayment'
      input={StyledInput}
      labelText={StyledText}
      className={styles.field}
      name={FORM_FIELD_NAMES.DOWNPAYMENT}
      type={TYPES.NUMBER}
      onChange={onChange}
      value={downPayment}
      placeholder='$0'
      min='0'
      step='1'
      required
    >
      <FormattedMessage
        id='Affordability.form.downPaymentLabel'
        defaultMessage='Down payment'
      />
      <Caption>
        <FormattedMessage
          id='Affordability.form.downPaymentSublabel'
          defaultMessage='The amount of cash you can put down on the house at closing.'
        />
      </Caption>
    </FormField>

    <FormField
      data-qa='selectLoanTerm'
      input={StyledSelect}
      labelText={StyledText}
      className={styles.field}
      name={FORM_FIELD_NAMES.LOANTERM}
      options={termOptions(intl)}
      onChange={value =>
        onChange({
          target: {
            name: FORM_FIELD_NAMES.LOANTERM,
            value
          }
        })
      }
      value={loanTerm}
    >
      <FormattedMessage
        id='Affordability.form.loanTermLabel'
        defaultMessage='Loan term'
      />
      <Caption>
        <FormattedMessage
          id='Affordability.form.loanTermSublabel'
          defaultMessage='How long it will take to pay off your new house'
        />
      </Caption>
    </FormField>

    <PriceCounter />

    <PersistAffordabilityButton className={styles.button} disabled={!valid} />
  </Fragment>
)

CalculatePricePointTab.propTypes = propTypes

export default CalculatePricePointTab
